// Pure freeform-canvas geometry (no DOM, no React) — the maths the v2 canvas runs
// to place, snap, and size widgets. Grid units in the store; pixels only for
// rendering + pointer math. Mirrors the prototype's STEP/GAP model so the feel
// matches Ben's design.

import { clampFreeform, type GridPosition } from "@catbento/shared";

/** One cell = 100px, with a 10px gutter → a 110px step between cells. */
export const CELL = 100;
export const GAP = 10;
export const STEP = CELL + GAP; // 110

/** Pixel span of `units` cells (n cells + n-1 gutters). */
export function pxSpan(units: number): number {
  return units * STEP - GAP;
}

/** A widget's pixel box from its grid position (top-left origin). */
export function unitsToPx(pos: GridPosition): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  return { left: pos.x * STEP, top: pos.y * STEP, width: pxSpan(pos.w), height: pxSpan(pos.h) };
}

/** Snap a pixel offset to the nearest cell index (non-negative). */
export function snapToCell(px: number): number {
  return Math.max(0, Math.round(px / STEP));
}

/** Snap a pixel span back to a whole cell count (>= 1). Adds GAP first so a box
 *  drawn `pxSpan(n)` wide rounds cleanly back to `n`. */
export function snapToSize(px: number): number {
  return Math.max(1, Math.round((px + GAP) / STEP));
}

/** Do two grid rectangles overlap at all? */
export function overlaps(a: GridPosition, b: GridPosition): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Which widget box contains `point`? `point` is in CANVAS coords (content-origin
 * relative, already un-scaled by the zoom). Topmost wins — later widgets paint on
 * top, so a drop resolves to the one you actually see. Returns the instanceId, or
 * null when the point is over empty canvas. Pure — the player-token drag uses it
 * to decide merge-into-a-widget vs create-a-Bio-on-empty-space.
 */
export function hitTestWidget(
  items: { instanceId: string; position: GridPosition }[],
  point: { x: number; y: number }
): string | null {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (!item) continue;
    const b = unitsToPx(item.position);
    if (
      point.x >= b.left &&
      point.x <= b.left + b.width &&
      point.y >= b.top &&
      point.y <= b.top + b.height
    ) {
      return item.instanceId;
    }
  }
  return null;
}

/**
 * Among `others`, the widget whose grid box overlaps `box` the most (by area), or
 * null if none overlap. Used by drag-to-merge: when a whole single-player card is
 * dragged over another, the card it covers most is the merge target. Pure.
 */
export function bestOverlapTarget(
  box: GridPosition,
  others: { instanceId: string; position: GridPosition }[]
): string | null {
  let bestId: string | null = null;
  let bestArea = 0;
  for (const o of others) {
    const ow = Math.min(box.x + box.w, o.position.x + o.position.w) - Math.max(box.x, o.position.x);
    const oh = Math.min(box.y + box.h, o.position.y + o.position.h) - Math.max(box.y, o.position.y);
    const area = Math.max(0, ow) * Math.max(0, oh);
    if (area > bestArea) {
      bestArea = area;
      bestId = o.instanceId;
    }
  }
  return bestId;
}

/**
 * Does a widget of `size` dropped at `cell` fit — i.e. sit fully in bounds and
 * NOT overlap any existing widget? Used by the palette drop: a fresh widget lands
 * only where it fits (green ghost); a spot that overlaps another widget is
 * rejected (red ghost, nothing dropped) rather than auto-relocated. Pure.
 */
export function placementFits(
  cell: { x: number; y: number },
  size: { w: number; h: number },
  existing: GridPosition[]
): boolean {
  const box: GridPosition = { x: cell.x, y: cell.y, w: size.w, h: size.h };
  const clamped = clampFreeform(box);
  // Out of bounds (clamp moved it) → doesn't fit as-dropped.
  if (clamped.x !== box.x || clamped.y !== box.y || clamped.w !== box.w || clamped.h !== box.h) {
    return false;
  }
  return !existing.some((e) => overlaps(box, e));
}

/**
 * Where to actually place a widget of `size` dropped at `cell`: clamp to the
 * canvas, then — if it would land exactly on an existing widget — nudge it down
 * a row at a time until it sits clear. Freeform otherwise (overlap from a manual
 * drag is allowed; this only stops a fresh drop hiding on top of another). Pure.
 */
export function resolveDropPosition(
  cell: { x: number; y: number },
  size: { w: number; h: number },
  existing: GridPosition[]
): GridPosition {
  let pos = clampFreeform({ x: cell.x, y: cell.y, w: size.w, h: size.h });
  // Nudge down past any widget it collides with; clampFreeform keeps y bounded,
  // so this can't loop forever.
  while (existing.some((e) => overlaps(pos, e))) {
    const next = clampFreeform({ ...pos, y: pos.y + 1 });
    if (next.y === pos.y) break; // hit the row cap — stop rather than spin
    pos = next;
  }
  return pos;
}
